import type { getClient } from '@retreats/supabase-adapter';
import { createChildLogger } from '@retreat/shared';
import { EditableRetreatScheduleSchema } from '@retreat/core';
import {
  loadUserSchedules,
  saveUserSchedule,
} from '../state/userScheduleRepository';
import type { UserSchedule } from '../state/userScheduleRepository';
import { reportError } from '../utils/reportError';

const log = createChildLogger('userScheduleSyncService');

const TABLE = 'user_schedules';

type SupabaseClient = ReturnType<typeof getClient>;

// ── Helpers ───────────────────────────────────────────────────────────────────

type UserScheduleInsert = {
  id: string;
  user_id: string;
  schedule: Record<string, unknown>;
  base_schedule_id: string | null;
  name: string;
  tradition: string | null;
  total_days: number;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
};

function toRow(entry: UserSchedule, userId: string): UserScheduleInsert {
  return {
    id: entry.schedule.id,
    user_id: userId,
    schedule: entry.schedule as Record<string, unknown>,
    base_schedule_id: entry.baseScheduleId,
    name: entry.schedule.name,
    tradition: entry.schedule.tradition ?? null,
    total_days: entry.schedule.days.length,
    is_deleted: entry.isDeleted,
    created_at: new Date(entry.createdAt).toISOString(),
    updated_at: new Date(entry.updatedAt).toISOString(),
  };
}

type RemoteRow = {
  id: string;
  schedule: unknown;
  base_schedule_id: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
};

function fromRow(row: RemoteRow): UserSchedule | null {
  const parsed = EditableRetreatScheduleSchema.safeParse(row.schedule);
  if (!parsed.success) {
    log.warn('Remote schedule failed Zod validation — skipping', { id: row.id });
    return null;
  }
  return {
    schedule: parsed.data,
    baseScheduleId: row.base_schedule_id,
    isDeleted: row.is_deleted,
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.updated_at).getTime(),
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Push all local user schedules (including soft-deleted) to Supabase.
 * Uses upsert by `id`; last-write-wins on `updated_at`.
 * Safe to call multiple times — idempotent.
 */
export async function pushUserSchedules(client: SupabaseClient, userId: string): Promise<void> {
  // Read all (including soft-deleted) so deletes propagate to other devices.
  const { get } = await import('idb-keyval');
  const { scheduleStore } = await import('../state/userScheduleRepository');
  const raw = await get<UserSchedule[]>('rc_user_schedules', scheduleStore) ?? [];

  if (raw.length === 0) return;

  const rows: UserScheduleInsert[] = raw.map((e) => toRow(e, userId));
  // DB types don't include user_schedules until the migration is applied and types are regenerated.
  // Cast through unknown to avoid 'as any'; replace with proper DB generic once types are generated.
  const { error } = await (client as unknown as { from(t: string): { upsert(d: UserScheduleInsert[], o: { onConflict: string }): Promise<{ error: { message: string } | null }> } }).from(TABLE).upsert(rows, { onConflict: 'id' });
  if (error) {
    reportError(new Error(error.message), 'SCHEDULE_PUSH_FAILED');
    log.warn('pushUserSchedules: upsert failed', { error: error.message });
    return;
  }
  log.info('pushUserSchedules: synced', { count: rows.length });
}

/**
 * Pull user schedules from Supabase and merge with local IDB store.
 * Remote row wins if its `updated_at` is newer than the local copy.
 * Adds new remote rows that don't exist locally.
 */
export async function pullUserSchedules(client: SupabaseClient, userId: string): Promise<void> {
  const { data, error } = await client
    .from(TABLE)
    .select('id, schedule, base_schedule_id, is_deleted, created_at, updated_at')
    .eq('user_id', userId);

  if (error) {
    reportError(new Error(error.message), 'SCHEDULE_PULL_FAILED');
    log.warn('pullUserSchedules: fetch failed', { error: error.message });
    return;
  }
  if (!data || data.length === 0) return;

  const local = await loadUserSchedules();
  const localById = new Map(local.map((e) => [e.schedule.id, e]));

  let merged = 0;
  for (const row of data as RemoteRow[]) {
    const remote = fromRow(row);
    if (!remote) continue;

    const existing = localById.get(row.id);
    if (!existing || remote.updatedAt > existing.updatedAt) {
      await saveUserSchedule(remote);
      merged++;
    }
  }

  log.info('pullUserSchedules: merged remote rows', { total: data.length, merged });
}

/**
 * Full two-way sync: push local → remote, then pull remote → local.
 * Called on login and on network restore. The Supabase client is injected
 * by the caller (SyncManager) so this function has no singleton dependency.
 */
export async function syncUserSchedules(client: SupabaseClient, userId: string): Promise<void> {
  await pushUserSchedules(client, userId);
  await pullUserSchedules(client, userId);
}
